import type { GridUrlState, UrlParams } from '../AgGridBase';

export const USERS_GRID_COLUMN_STATE_KEY = 'posture-hub-users-ag-grid-column-state';

export const USERS_GRID_URL_KEYS = ['department', 'organization', 'title', 'sortColumn', 'sortOrder', 'q'] as const;

/** Default column layout when no saved state exists (visibility, width). */
export const USERS_GRID_DEFAULT_COLUMN_STATE = [
  { colId: 'score', hide: false, width: 120 },
  { colId: 'firstName', hide: false, width: 220 },
  { colId: 'topRiskContributors', hide: false, width: 240 },
  { colId: 'department', hide: false, width: 200 },
  { colId: 'title', hide: false, width: 180 },
  { colId: 'startDate', hide: false, width: 120 },
  { colId: 'organization', hide: false, width: 180 },
] as const;

export const USERS_GRID_CONFIG = {
  cacheBlockSize: 20,
  defaultHeight: '100%',
  minHeight: '200px',
} as const;

/** Stable signature of URL fields that affect the users list query (for SSRM refresh). */
export function usersUrlDataSignature(urlParams: UrlParams): string {
  return JSON.stringify({
    q: urlParams?.q,
    sortColumn: urlParams?.sortColumn,
    sortOrder: urlParams?.sortOrder,
    department: urlParams?.department,
    organization: urlParams?.organization,
    title: urlParams?.title,
  });
}

export type UsersGridUrlState = GridUrlState;

/** Maps URL query params (useSearchParamsState) to AG Grid filter/sort models. */
export function mapUrlParamsToUsersGridState(urlParams: UrlParams): UsersGridUrlState {
  const sortModel: UsersGridUrlState['sortModel'] = [];
  const sortColumn = urlParams.sortColumn;
  const sortOrder = urlParams.sortOrder;
  const q = urlParams.q as string | undefined;
  if (typeof sortColumn === 'string' && sortColumn) {
    sortModel.push({
      colId: sortColumn,
      sort: typeof sortOrder === 'string' && sortOrder === 'DESC' ? 'desc' : 'asc',
    });
  } else {
    sortModel.push({ colId: 'score', sort: 'asc' });
  }

  const filterModel: Record<string, unknown> = {};

  const setParam = (urlKey: 'department' | 'organization' | 'title', modelKey: string) => {
    const raw = urlParams[urlKey];
    const values = Array.isArray(raw) ? raw : raw !== undefined && raw !== null && raw !== '' ? [String(raw)] : [];
    if (values.length > 0) {
      filterModel[modelKey] = { filterType: 'set', values };
    }
  };

  setParam('department', 'department');
  setParam('organization', 'organization');
  setParam('title', 'title');

  return { sortModel, filterModel, q: q ?? undefined };
}

/** Maps AG Grid filter/sort state to URL params (useSearchParamsState). */
export function mapUsersGridStateToUrlParams(gridState: UsersGridUrlState): UrlParams {
  const params: UrlParams = {};

  if (Array.isArray(gridState.sortModel) && gridState.sortModel.length > 0) {
    const s = gridState.sortModel[0];
    params.sortColumn = s.colId;
    params.sortOrder = s.sort?.toUpperCase() === 'DESC' ? 'DESC' : 'ASC';
  }

  const fm = gridState.filterModel ?? {};
  (['department', 'organization', 'title'] as const).forEach((key) => {
    const model = fm[key] as { filterType?: string; values?: string[] } | undefined;
    if (model?.filterType === 'set' && Array.isArray(model.values) && model.values.length > 0) {
      params[key] = model.values;
    }
  });

  if (gridState.q) {
    params.q = gridState.q;
  }

  return params;
}
